

























import { computed, defineComponent, ref, useContext, useFetch, useRoute } from '@nuxtjs/composition-api';
import DynamicNavigation from '~/components/Navigation/DynamicNavigation.vue';
import { useApi } from '~/composables';
import { getAnalysisPageMetaInfo } from '~/helpers/getAnalysisPageMetaInfo';
import type { ProductDetailsQuery } from '~/modules/GraphQL/types';
import { usePageStore } from '~/stores/page';
import ProductAnalyses from '../product/components/ProductAnalyses.vue';
import ProductPreparations from '../product/components/ProductPreparations.vue';
import AnalysisSkeleton from '../product/components/loading/AnalysisSkeleton.vue';
import getAnalysisPreparationsGql from '../product/queries/getAnalysisPreparations.gql';
import { useAnalysesStore } from '../product/stores/analyses';
import { Breadcrumb, CustomProduct, Preparations } from '../types';
import Analysis from './analysis.vue';

export default defineComponent({
  name: 'AnalysesListPage',
  components: {
    ProductAnalyses,
    ProductPreparations,
    Analysis,
    DynamicNavigation,
    AnalysisSkeleton,
  },
  middleware: ['analyses'],
  setup() {
    const pageStore = usePageStore();
    const { localePath, i18n } = useContext();
    const route = useRoute();
    const { query } = useApi();
    const analysesStore = useAnalysesStore();
    const analysesCategories = computed(() => analysesStore.analysesCategories);
    const defaultAnalysis = computed(() => analysesStore.defaultAnalysis);
    const analysesCount = computed(() => analysesStore.analysesCount);
    const hasChildRoute = computed(() => Boolean(route.value.params?.slug));
    const packageName = computed<string>(() => pageStore.parentRouteData?.name || '');
    const preparations = ref<Preparations | null>({});
    const parentPackageLink = computed(() => {
      if (!packageName.value) return '';
      return localePath(`/analyspaket/${route.value.params?.parentSlug}`);
    });
    const getActiveProduct = (): CustomProduct | null => {
      const slug = route.value.params?.slug;
      if (slug) {
        let currentActiveProduct: CustomProduct | null = null;
        analysesCategories.value.find((category) =>
          category.products?.items?.some((product: CustomProduct) => {
            if (product.url_key === slug || (pageStore.routeData?.sku && pageStore.routeData.sku === product.sku)) {
              currentActiveProduct = product;
              return true;
            }
            return false;
          }),
        );
        return currentActiveProduct;
      }

      return (defaultAnalysis.value || {}) as CustomProduct;
    };
    const activeProduct = computed(() => getActiveProduct());

    const DEFAULT_TITLE = i18n.t('Analyses List') as string;

    const title = computed(() => {
      if (packageName.value && typeof packageName.value === 'string') {
        const analysisText = analysesCount.value ? `(${analysesCount.value} ${i18n.t('analyses')})` : '';
        return `${packageName.value} ${analysisText}`;
      }
      return DEFAULT_TITLE;
    });

    const fetchPreparations = async () => {
      const slug = route.value.params?.slug;
      const sku = activeProduct.value?.sku || pageStore.routeData?.sku || defaultAnalysis.value?.sku;
      const filters = slug
        ? [{ url_key: { eq: slug } }, ...(sku ? [{ sku: { eq: sku } }] : [])]
        : [{ sku: { eq: sku } }];

      for (const filter of filters) {
        const { data } = await query<ProductDetailsQuery>(getAnalysisPreparationsGql, { filter });
        const nextPreparations = data.products.items?.[0] as Preparations | undefined;
        if (nextPreparations) {
          preparations.value = nextPreparations;
          return;
        }
      }

      preparations.value = {};
    };

    const mobileNavigation = computed(() => {
      /*
      Mobile navigation:
      1. Analyses/Individual analysis: Analysislist; back arrow to it | simple analysis
      2. Package Analyses: Package Name; back arrow to it | package analyses
      3. Package Analyses/Indiv analysis: prod/Analyslist; back arrow to it | simple package analysis
    */
      const [routeName, locale] = route.value.name.split('___'); // eslint-disable-line @typescript-eslint/no-unused-vars
      switch (routeName) {
        case 'simple analysis':
          return {
            mobileBreadcrumb: {
              text: DEFAULT_TITLE,
              link: localePath('/alla-analyser'),
            },
            withNavigationBackArrow: true,
          };
        case 'package analyses':
          return {
            mobileBreadcrumb: {
              text: packageName.value,
              link: parentPackageLink.value,
            },
            withNavigationBackArrow: true,
          };
        case 'simple package analysis':
          return {
            mobileBreadcrumb: {
              text: DEFAULT_TITLE,
              link: localePath(`/${route.value.params?.parentSlug}/analys`),
            },
            withNavigationBackArrow: true,
          };
        default:
          throw new Error('Invalid Route Name for Analyses List page');
      }
    });

    const breadcrumbs = computed<Breadcrumb[]>(() => [
      {
        text: i18n.t('Home') as string,
        link: localePath('/'),
      },
      ...(!packageName.value
        ? [
            {
              text: DEFAULT_TITLE,
              link: localePath('/alla-analyser'),
            },
          ]
        : []),
      ...(packageName.value
        ? [
            {
              text: i18n.t('Analysis packages') as string,
              link: localePath('/provanalyser'),
            },
          ]
        : []),
      ...(packageName.value
        ? [
            {
              text: packageName.value,
              link: localePath(`/analyspaket/${route.value.params?.parentSlug}`),
            },
          ]
        : []),
      ...(packageName.value
        ? [
            {
              text: DEFAULT_TITLE,
              link: localePath(`/${route.value.params?.parentSlug}/analys`),
            },
          ]
        : []),
      ...(hasChildRoute.value
        ? [
            {
              text: getActiveProduct()?.name,
              link: route.value.path,
            },
          ]
        : []),
    ]);

    useFetch(async () => {
      await analysesStore.fetchProducts(pageStore.parentRouteData?.sku as string | undefined);
      await fetchPreparations();
    });

    return {
      breadcrumbs,
      mobileNavigation,
      analysesCategories,
      preparations,
      hasChildRoute,
      title,
      defaultAnalysis,
      packageName,
      activeProduct,
      activeProductSku: computed(() => activeProduct.value?.sku),
      parentPackageLink,
    };
  },
  head() {
    if (this.hasChildRoute) {
      return {};
    }

    return getAnalysisPageMetaInfo({ page: this.activeProduct, isListPage: true, packageName: this.packageName as string });
  },
});
